// Analytics Dashboard — Smart RFQ System
import React, { useState } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  BarChart3, TrendingUp, Activity, Target, Filter, ArrowRight,
  Clock, CheckCircle2, AlertTriangle, Users, Globe, Layers, Zap
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  ANALYTICS_RFQS, getStatusCounts, getConversionFunnel, getAverageResponseTime,
  getRFQsByService, getRFQsByCountry, getRFQsByCondition,
  getCompletenessMetrics, getTotalRevisionRequests, getClientTypeDistribution,
  getPriorityDistribution, getTopPriorityRFQs, calculatePriorityScore,
} from './analyticsData';

type TabKey = 'commercial' | 'operations' | 'quality';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'commercial', label: 'Commercial Performance', icon: <TrendingUp className="w-4 h-4" /> },
  { key: 'operations', label: 'Operational Demand', icon: <Activity className="w-4 h-4" /> },
  { key: 'quality', label: 'Quality & Behavior', icon: <Target className="w-4 h-4" /> },
];

const CHART_COLORS = ['#f97316', '#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#06b6d4', '#eab308'];
const PIE_COLORS = ['#f97316', '#3b82f6', '#22c55e'];

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────

export const AnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('commercial');

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <div>
              <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-orange-400" />
                RFQ Analytics
              </h1>
              <p className="text-slate-400 text-sm mt-1">Operational & Commercial Decision Intelligence — Last 30 Days</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-slate-300 text-sm">Period: Apr 1 – May 1, 2026</span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-slate-800/40 p-1 rounded-xl mb-8 border border-slate-700/50">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                }`}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'commercial' && <CommercialTab />}
          {activeTab === 'operations' && <OperationsTab />}
          {activeTab === 'quality' && <QualityTab />}
        </div>
      </div>
    </Layout>
  );
};

// ─── COMMERCIAL PERFORMANCE TAB ──────────────────────────────────────────────

const CommercialTab: React.FC = () => {
  const statusCounts = getStatusCounts();
  const funnel = getConversionFunnel();
  const avgResponse = getAverageResponseTime();
  const total = ANALYTICS_RFQS.length;

  const statusData = [
    { name: 'New', value: statusCounts.submitted, color: '#3b82f6' },
    { name: 'Under Review', value: statusCounts['under-review'], color: '#f59e0b' },
    { name: 'Proposal Generated', value: statusCounts['proposal-generated'], color: '#22c55e' },
    { name: 'Closed', value: statusCounts.closed, color: '#64748b' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Total RFQs" value={total.toString()} sub="Last 30 days" icon={<Layers className="w-5 h-5 text-blue-400" />} />
        <KPICard label="Avg Response Time" value={`${avgResponse}h`} sub="Submission to proposal" icon={<Clock className="w-5 h-5 text-amber-400" />} />
        <KPICard label="Proposals Sent" value={ANALYTICS_RFQS.filter(r => r.proposalGenerated).length.toString()} sub={`${Math.round((ANALYTICS_RFQS.filter(r => r.proposalGenerated).length / total) * 100)}% conversion`} icon={<CheckCircle2 className="w-5 h-5 text-green-400" />} />
        <KPICard label="Client Responses" value={ANALYTICS_RFQS.filter(r => r.clientResponded).length.toString()} sub={`${Math.round((ANALYTICS_RFQS.filter(r => r.clientResponded).length / total) * 100)}% response rate`} icon={<Users className="w-5 h-5 text-purple-400" />} />
      </div>

      {/* Status Breakdown + Funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-orange-400" /> RFQ Status Distribution
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#e2e8f0', fontSize: 12 }} axisLine={false} tickLine={false} width={120} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {statusData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-orange-400" /> Conversion Funnel
          </h3>
          <div className="space-y-3">
            {funnel.map((step, idx) => (
              <div key={step.stage}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {idx < funnel.length - 1 && <ArrowRight className="w-3 h-3 text-slate-500" />}
                    <span className="text-slate-200 text-sm">{step.stage}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm">{step.count}</span>
                    <span className="text-slate-400 text-xs">({step.pct}%)</span>
                  </div>
                </div>
                <div className="h-6 bg-slate-700/50 rounded-lg overflow-hidden">
                  <div
                    className="h-full rounded-lg transition-all duration-700"
                    style={{
                      width: `${step.pct}%`,
                      background: `linear-gradient(90deg, ${CHART_COLORS[idx]}, ${CHART_COLORS[idx]}88)`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── OPERATIONAL DEMAND TAB ──────────────────────────────────────────────────

const OperationsTab: React.FC = () => {
  const byService = getRFQsByService();
  const byCountry = getRFQsByCountry();
  const byCondition = getRFQsByCondition();

  return (
    <div className="space-y-6">
      {/* Service Type Chart */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Layers className="w-4 h-4 text-orange-400" /> RFQs by Service Type
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={byService} margin={{ bottom: 60 }}>
            <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} angle={-25} textAnchor="end" interval={0} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }} labelStyle={{ color: '#f1f5f9' }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>
              {byService.map((_, idx) => (
                <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Country + Conditions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Country Distribution */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-orange-400" /> RFQs by Country
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={byCountry}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, value }) => `${name} (${value})`}
                labelLine={{ stroke: '#64748b' }}
              >
                {byCountry.map((_, idx) => (
                  <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }} />
              <Legend wrapperStyle={{ color: '#e2e8f0', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Operating Conditions */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" /> RFQs by Operating Conditions
          </h3>
          <div className="space-y-5 mt-6">
            {byCondition.map((cond, idx) => (
              <div key={cond.name}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-200 text-sm">{cond.name}</span>
                  <span className="text-white font-semibold">{cond.value} <span className="text-slate-400 text-xs font-normal">/ {ANALYTICS_RFQS.length}</span></span>
                </div>
                <div className="h-4 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${(cond.value / ANALYTICS_RFQS.length) * 100}%`,
                      background: idx === 0 ? '#ef4444' : idx === 1 ? '#f59e0b' : '#a855f7',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── QUALITY & BEHAVIOR TAB ──────────────────────────────────────────────────

const QualityTab: React.FC = () => {
  const completeness = getCompletenessMetrics();
  const totalRevisions = getTotalRevisionRequests();
  const clientTypes = getClientTypeDistribution();
  const priorityDist = getPriorityDistribution();
  const topRFQs = getTopPriorityRFQs();

  return (
    <div className="space-y-6">
      {/* Quality KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          label="Complete RFQs"
          value={`${completeness.pct}%`}
          sub={`${completeness.complete} of ${completeness.total} RFQs ≥80% filled`}
          icon={<CheckCircle2 className="w-5 h-5 text-green-400" />}
        />
        <KPICard
          label="Revision Requests"
          value={totalRevisions.toString()}
          sub="Total revision requests received"
          icon={<AlertTriangle className="w-5 h-5 text-amber-400" />}
        />
        <KPICard
          label="Avg Completeness"
          value={`${Math.round(ANALYTICS_RFQS.reduce((s, r) => s + r.completeness, 0) / ANALYTICS_RFQS.length)}%`}
          sub="Average data completeness score"
          icon={<Target className="w-5 h-5 text-blue-400" />}
        />
      </div>

      {/* Client Type + Priority */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Type Distribution */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-orange-400" /> Client Type Distribution
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={clientTypes}
                cx="50%"
                cy="50%"
                outerRadius={80}
                dataKey="value"
                label={({ name, value }) => `${name} (${value})`}
                labelLine={{ stroke: '#64748b' }}
              >
                {clientTypes.map((_, idx) => (
                  <Cell key={idx} fill={PIE_COLORS[idx]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }} />
              <Legend wrapperStyle={{ color: '#e2e8f0', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Priority Scoring Distribution */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-400" /> Priority Classification
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={priorityDist}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={80}
                dataKey="value"
                label={({ name, value }) => `${name} (${value})`}
                labelLine={{ stroke: '#64748b' }}
              >
                {priorityDist.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }} />
              <Legend wrapperStyle={{ color: '#e2e8f0', fontSize: '12px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Priority Queue Table */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Target className="w-4 h-4 text-orange-400" /> Priority Queue — Top Scored RFQs
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left text-slate-400 font-medium py-3 px-2">RFQ</th>
                <th className="text-left text-slate-400 font-medium py-3 px-2">Client</th>
                <th className="text-left text-slate-400 font-medium py-3 px-2">Service</th>
                <th className="text-left text-slate-400 font-medium py-3 px-2">Score</th>
                <th className="text-left text-slate-400 font-medium py-3 px-2">Priority</th>
                <th className="text-left text-slate-400 font-medium py-3 px-2">Factors</th>
              </tr>
            </thead>
            <tbody>
              {topRFQs.map(rfq => (
                <tr key={rfq.id} className="border-b border-slate-700/40 hover:bg-slate-700/20">
                  <td className="py-3 px-2 text-white font-mono text-xs">{rfq.id}</td>
                  <td className="py-3 px-2">
                    <div className="text-slate-200">{rfq.client}</div>
                    <div className="text-slate-500 text-xs">{rfq.company}</div>
                  </td>
                  <td className="py-3 px-2 text-slate-300">{rfq.serviceLabel}</td>
                  <td className="py-3 px-2">
                    <span className="text-white font-bold">{rfq.score}</span>
                    <span className="text-slate-500 text-xs">/100</span>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      rfq.level === 'high' ? 'bg-red-500/20 text-red-300' :
                      rfq.level === 'medium' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-green-500/20 text-green-300'
                    }`}>
                      {rfq.level.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-wrap gap-1">
                      {rfq.factors.slice(0, 2).map((f, i) => (
                        <span key={i} className="text-xs text-slate-400 bg-slate-700/50 px-2 py-0.5 rounded">{f}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── SHARED COMPONENTS ───────────────────────────────────────────────────────

const KPICard: React.FC<{ label: string; value: string; sub: string; icon: React.ReactNode }> = ({ label, value, sub, icon }) => (
  <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 bg-slate-700/50 rounded-lg">{icon}</div>
      <span className="text-slate-400 text-sm">{label}</span>
    </div>
    <div className="text-3xl font-bold text-white font-['Oswald']">{value}</div>
    <p className="text-slate-500 text-xs mt-1">{sub}</p>
  </div>
);

export default AnalyticsDashboard;
